import { addCollection } from '@iconify/react';
import { useSyncExternalStore } from 'react';
import type { IconifyJSON } from '@iconify/types';
import { ICON_PREFIX } from './fileIconName';

let ready = false;
let started = false;
const available = new Set<string>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Strip the `vscode-icons:` prefix to get the bare collection key. */
function toKey(name: string): string {
  const p = `${ICON_PREFIX}:`;
  return name.startsWith(p) ? name.slice(p.length) : name;
}

/**
 * Register an IconifyJSON collection synchronously. Used by the lazy loader and
 * by tests. Marks the set started+ready so the lazy import never double-fires.
 */
export function registerIconSetData(data: IconifyJSON): void {
  addCollection(data);
  for (const key of Object.keys(data.icons)) available.add(key);
  if (data.aliases) {
    for (const key of Object.keys(data.aliases)) available.add(key);
  }
  started = true;
  ready = true;
  notify();
}

/**
 * Kick off the one-time lazy import of the bundled vscode-icons set. Idempotent.
 * Keeps the (large) icon JSON out of the initial chunk. On failure the set stays
 * unready and components keep rendering their Lucide fallback.
 */
export function ensureIconSet(): void {
  if (started) return;
  started = true;
  void import('@iconify-json/vscode-icons/icons.json')
    .then((mod) => {
      const data = (mod.default ?? mod) as unknown as IconifyJSON;
      // registerIconSetData sets `started` again (already true) — harmless.
      registerIconSetData(data);
    })
    .catch(() => {
      // Leave unready; FileTypeIcon falls back to Lucide.
    });
}

/** True once a collection has been registered. */
export function isIconSetReady(): boolean {
  return ready;
}

/** True when `name` (an Iconify name or bare key) is in the registered set. */
export function hasIcon(name: string): boolean {
  return available.has(toKey(name));
}

/** Subscribe to readiness changes. Returns an unsubscribe fn. */
export function subscribeIconSet(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React hook: re-renders when the icon set becomes ready. */
export function useIconSetReady(): boolean {
  return useSyncExternalStore(subscribeIconSet, isIconSetReady, isIconSetReady);
}

/** TEST ONLY: reset module state so cases start from a clean slate. */
export function resetIconSetForTests(): void {
  ready = false;
  started = false;
  available.clear();
  listeners.clear();
}
